import {useCallback} from 'react';

import type {Tag} from 'shells/tags-editor/tags-editor';

import type {AccountPageFragment, NutFileInput} from 'nutshell-graphql-types';

import {useHardDeleteAccount} from '../graphql/hooks/use-hard-delete-account';
import {useUpdateAccount} from '../graphql/hooks/use-update-account';

type Result = {
    handleUpdateName: (name: string) => Promise<any>;
    handleUpdateAvatar: (avatarFile: NutFileInput | null) => Promise<any>;
    handleUpdateAssignee: (assigneeId: string | null) => Promise<any>;
    handleUpdateDeletedStatus: (isDeleted: boolean) => Promise<any>;
    handleUpdateDescription: (description: string) => Promise<any>;
    handleHardDelete: () => Promise<any>;
    handleUpdateAccountTerritory: (
        territoryId: string | null,
        territoryName: string | null
    ) => Promise<any>;
    handleUpdateAccountType: (accountTypeId: string, accountTypeName: string) => Promise<any>;
    handleUpdateAccountIndustry: (
        industryId: string | null,
        industryName: string | null
    ) => Promise<any>;
    handleUpdateAccountRevenue: (revenue: number) => Promise<any>;
    handleUpdateAccountEmployeeCount: (employeeCount: number) => Promise<any>;
    handleUpdateTags: (tags: Tag[]) => Promise<any>;
};

export function useUpdateAccountHandlers(account?: AccountPageFragment | null): Result {
    const {updateAccount} = useUpdateAccount();
    const {hardDeleteAccount} = useHardDeleteAccount();

    const handleUpdateName = useCallback(
        (name: string) => {
            if (!account) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: account.id,
                    name,
                },
                account
            );
        },
        [updateAccount, account]
    );

    const handleUpdateAvatar = useCallback(
        (avatarFile: NutFileInput | null) => {
            if (!account) {
                return Promise.resolve();
            }

            return updateAccount({
                accountId: account.id,
                avatarFile,
            });
        },
        [updateAccount, account]
    );

    const handleUpdateAssignee = useCallback(
        (assigneeId: string | null) => {
            if (!account) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: account.id,
                    assigneeId,
                },
                account
            );
        },
        [updateAccount, account]
    );

    const handleUpdateDeletedStatus = useCallback(
        (isDeleted: boolean) => {
            if (!account) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: account.id,
                    isDeleted,
                },
                account
            );
        },
        [updateAccount, account]
    );

    const handleUpdateDescription = useCallback(
        (description: string) => {
            if (!account) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: account.id,
                    description,
                },
                account
            );
        },
        [updateAccount, account]
    );

    const handleUpdateAccountTerritory = useCallback(
        (territoryId: string | null, territoryName: string | null) => {
            if (!account) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: account.id,
                    territoryId,
                },
                account,
                {territoryName}
            );
        },
        [updateAccount, account]
    );
    const handleUpdateAccountType = useCallback(
        (accountTypeId: string, accountTypeName: string) => {
            if (!account) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: account.id,
                    accountTypeId,
                },
                account,
                {accountTypeName}
            );
        },
        [updateAccount, account]
    );

    const handleUpdateAccountIndustry = useCallback(
        (industryId: string | null, industryName: string | null) => {
            if (!account) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: account.id,
                    industryId,
                },
                account,
                {industryName}
            );
        },
        [updateAccount, account]
    );

    const handleUpdateAccountRevenue = useCallback(
        (revenue: number) => {
            if (!account) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: account.id,
                    revenue,
                },
                account
            );
        },
        [updateAccount, account]
    );

    const handleUpdateAccountEmployeeCount = useCallback(
        (employeeCount: number) => {
            if (!account) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: account.id,
                    employeeCount,
                },
                account
            );
        },
        [updateAccount, account]
    );

    const handleHardDelete = useCallback(() => {
        if (!account) {
            return Promise.resolve();
        }

        return hardDeleteAccount(account.id).then(() => {
            // force page reload to remove deleted account from list and search results
            window.location.replace(`/companies`);
        });
    }, [hardDeleteAccount, account]);

    const handleUpdateTags = useCallback(
        (tags: Tag[]) => {
            if (!account) {
                return Promise.resolve();
            }

            return updateAccount(
                {
                    accountId: account.id,
                    tags: tags.map((tag) => ({
                        name: tag.name,
                        tagType: 'ACCOUNT',
                    })),
                },
                account,
                undefined, // optimisticNames
                tags
            );
        },
        [updateAccount, account]
    );

    return {
        handleUpdateName,
        handleUpdateAvatar,
        handleUpdateAssignee,
        handleUpdateDeletedStatus,
        handleUpdateDescription,
        handleHardDelete,
        handleUpdateAccountTerritory,
        handleUpdateAccountType,
        handleUpdateAccountIndustry,
        handleUpdateAccountRevenue,
        handleUpdateAccountEmployeeCount,
        handleUpdateTags,
    };
}
